import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom/client';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import { ChevronLeft, ChevronRight, Calendar, List, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import useAxiosInstance from '../../lib/useAxiosInstance';
import { useMutation } from 'react-query';
import "../../Styles/overview2.css";

const CalendarComponent = ({
  onToggleView,
  onDateSelect,
  initialView = 'dayGridMonth',
  selectedDate,
  events,
  dndEntries = [],
  refetchDND,
  onEventClick
}) => {
  const axiosInstance=useAxiosInstance()
  const user = JSON.parse(localStorage.getItem("user"));
  const calendarRef = useRef(null);
  const [currentView, setCurrentView] = useState(initialView);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [headerDate, setHeaderDate] = useState(new Date());
  const [blockedSlots, setBlockedSlots] = useState([]);

  const { mutateAsync: removeDND, isLoading: dndDeleting } = useMutation(
    async (dndId) => {
      await axiosInstance.delete(`api/eventScheduler/deleteDND/${dndId}`);
      return dndId;
    },
    {
      onSuccess: () => {
        toast.success('Blocked slot removed');
        /* ask Dashboard to pull fresh list */
        refetchDND && refetchDND();
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || 'Failed to remove DND');
      }
    }
  );

  useEffect(() => {
    // Convert DND entries to blocked slots format
    const blocked = dndEntries.map(dnd => ({
      start: new Date(dnd.start),
      end: new Date(dnd.end),
      isAllDay: dnd.start?.includes('T00:00:00') && dnd.end?.includes('T23:59:59')
    }));
    setBlockedSlots(blocked);
  }, [dndEntries]);

  // Check if a date/time is blocked
  const isTimeBlocked = (date, startTime = null, endTime = null) => {
    const checkDate = new Date(date);
    
    return blockedSlots.some(blocked => {
      // Check for all-day blocks
      if (blocked.isAllDay) {
        const blockedDate = new Date(blocked.start);
        return checkDate.toDateString() === blockedDate.toDateString();
      }
      
      // Check for time-specific blocks
      if (startTime && endTime) {
        const startDateTime = new Date(`${date}T${startTime}`);
        const endDateTime = new Date(`${date}T${endTime}`);
        
        return (
          (startDateTime >= blocked.start && startDateTime < blocked.end) ||
          (endDateTime > blocked.start && endDateTime <= blocked.end) ||
          (startDateTime <= blocked.start && endDateTime >= blocked.end)
        );
      }
      
      return false;
    });
  };

  const deleteDndEntry = useCallback(
    async (dndId) => {
      try {
        await axiosInstance.delete(`api/eventScheduler/deleteDND/${dndId}`);
        toast.success('Blocked slot removed');
        return true;
      } catch (err) {
        console.error(err);
        toast.error(err?.response?.data?.message || 'Failed to remove DND');
        return false;
      }
    },
    [axiosInstance]
  );

  const injectUnblockBtn = useCallback(
  (info) => {
    if (!info.event.extendedProps.isDND) return;
    if (info.event.extendedProps.isDND) {
        info.el.style.pointerEvents = 'auto';
        info.el.style.opacity = '0.8';
      }
    const dndId = info.event.id.replace('dnd-', '');

    const btnContainer = document.createElement('div');
    btnContainer.style.position = 'absolute';
    btnContainer.style.top = '4px';
    btnContainer.style.right = '4px';
    btnContainer.style.zIndex = '10';

    const root = ReactDOM.createRoot(btnContainer);
    if(user?.role!=='data_entry_operator')
    {
      root.render(
      <button
        onClick={async (e) => {
          e.stopPropagation();
          /* run the mutation */
          await removeDND(dndId);
          info.event.remove();
        }}
        className="flex items-center space-x-1 text-xs px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded shadow"
      >
        <Trash2 className="w-3 h-3" />
        <span>Unblock</span>
      </button>
    );
    }
    

    info.el.appendChild(btnContainer);
  },
  [deleteDndEntry]
);

  const mappedEvents = events
  .filter((e) => e.status === 'pending' || e.status === 'approved')
  .map((e) => {

    // ✅ Status color mapping
    const statusColors = {
      pending: {
        backgroundColor: '#FDE68A', 
        borderColor: '#FBBF24',
        textColor: '#92400E',
      },
      approved: {
        backgroundColor: '#15803D', 
        borderColor: '#166534',
        textColor: '#D1FAE5',
      },
    };
     // ✅ Dot color for typeOfEvent (used in Day view dots)
    const dotColors = {
      visit: '#3B82F6', // blue
      invitation: '#EC4899', // pink
      appointment: '#10B981', // green
    };

    const statusColor = statusColors[e.status] || {
      backgroundColor: '#D1D5DB',
      borderColor: '#9CA3AF',
    };

    // const colorMap = {
    //   visit: { backgroundColor: '#60A5FA', borderColor: '#3B82F6' },
    //   invitation: { backgroundColor: '#F472B6', borderColor: '#EC4899' },
    //   appointment: { backgroundColor: '#34D399', borderColor: '#10B981' },
    // };

    // const color = colorMap[e.typeOfEvent] || {
    //   backgroundColor: '#A78BFA',
    //   borderColor: '#8B5CF6',
    // };

    // return {
    //   id: e._id,
    //   title: `${e.eventTitle} (${e.typeOfEvent})`,
    //   start: e.start,
    //   end: e.end,
    //   ...color,
    //   textColor: '#ffffff',
    // };
    return {
      id: e._id,
      title: `${e.eventTitle} (${e.typeOfEvent})`,
      start: e.start,
      end: e.end,
      textColor: '#ffffff',
      extendedProps: {
        typeOfEvent: e.typeOfEvent,
        dotColor: dotColors[e.typeOfEvent],
        status: e.status,
      },
      ...statusColor,
    };
  });

  // Add DND entries as events
  const dndEvents = dndEntries.map(dnd => ({
    id: `dnd-${dnd._id}`,
    title: 'Blocked',
    start: dnd.start,
    end: dnd.end,
    backgroundColor: '#9CA3AF',
    borderColor: '#6B7280',
    textColor: '#374151',
    display: 'background',
    classNames: ['dnd-event'],
    extendedProps: {
      isDND: true
    }
  }));

  const allEvents = [...mappedEvents, ...dndEvents];

  useEffect(() => {
    if (selectedDate) {
      setHeaderDate(new Date(selectedDate));
    }
  }, [selectedDate]);

  useEffect(() => {
    if (initialView !== currentView) {
      setCurrentView(initialView);
      const calendarApi = calendarRef.current?.getApi();
      calendarApi?.changeView(initialView);
    }
  }, [initialView]);


  useEffect(() => {
  const calendarApi = calendarRef.current?.getApi();

  if (selectedDate && calendarApi) {
    calendarApi.gotoDate(selectedDate);
    if (initialView !== currentView) {
      calendarApi.changeView(initialView);
      setCurrentView(initialView);
    }
  }
}, [selectedDate, initialView]);

  const handleDateSelect = (selectInfo) => {
  const calendarApi = calendarRef.current?.getApi();
  const viewType   = calendarApi?.view?.type;

  // YYYY‑MM‑DD
  const selectedDate = selectInfo.startStr.split('T')[0];

  // HH:MM (24‑h)
  const startDate         = new Date(selectInfo.start);
  const endDate           = new Date(selectInfo.end);          // ⬅️ where the mouse was released
  const selectedStartTime = startDate.toTimeString().slice(0, 5);
  const selectedEndTime   = endDate  .toTimeString().slice(0, 5);

  // Check if the selected time is blocked
  if (viewType === 'timeGridWeek' || viewType === 'timeGridDay') {
    if (isTimeBlocked(selectedDate, selectedStartTime, selectedEndTime)) {
      toast.error('This date and time is blocked and cannot be selected');
      return;
    }
  }

  // Check if the selected date is blocked (all day)
  if (viewType === 'dayGridMonth') {
    if (isTimeBlocked(selectedDate)) {
      toast.error('This date is blocked and cannot be selected');
      return;
    }
  }

  /* Week / Day views → open modal with BOTH times */
  if (viewType === 'timeGridWeek' || viewType === 'timeGridDay') {
    onDateSelect(selectedDate, selectedStartTime, selectedEndTime);
  }

  /* Month view → just switch to Day view (no modal) */
  if (viewType === 'dayGridMonth') {
    calendarApi.changeView('timeGridDay', selectInfo.start);
    setCurrentView('timeGridDay');
    setCurrentDate(selectInfo.start);
  }
};

  const handlePrevClick = () => {
    const calendarApi = calendarRef.current?.getApi();
    calendarApi?.prev();
    const newDate = calendarApi?.getDate() || new Date();
    setCurrentDate(newDate);
    setHeaderDate(newDate);
  };

  const handleNextClick = () => {
    const calendarApi = calendarRef.current?.getApi();
    calendarApi?.next();
    const newDate = calendarApi?.getDate() || new Date();
    setCurrentDate(newDate);
    setHeaderDate(newDate);
  };

  const handleTodayClick = () => {
    const calendarApi = calendarRef.current?.getApi();
    calendarApi?.today();
    const today = calendarApi?.getDate() || new Date();
    setCurrentDate(today);
    setHeaderDate(today);
  };

  const handleViewChange = (view) => {
    const calendarApi = calendarRef.current?.getApi();
    calendarApi?.changeView(view);
    const newDate = calendarApi?.getDate() || new Date();
    setCurrentView(view);
    setCurrentDate(newDate);
    setHeaderDate(newDate);
  };

 const getMonthYear = () =>
    headerDate.toLocaleDateString("default", {
      month: "long",
      year: "numeric",
    });

  const getFullDateWithDay = () =>
    headerDate.toLocaleDateString("default", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  return (
    <div className="bg-white/20 backdrop-blur-md rounded-2xl border border-white/30 shadow-lg overflow-hidden">
      {/* Calendar Header */}
      <div className="p-6 border-b border-white/20">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <div className="p-2 bg-blue-500/10 rounded-xl backdrop-blur-sm">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Calendar</h2>
              <p className="text-lg text-gray-700 font-medium">{getMonthYear()}</p>
              <p className="text-sm text-gray-600">{getFullDateWithDay()}</p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={onToggleView}
              className="flex items-center space-x-2 px-4 py-2 bg-[var(--polstrat-primary)] text-white rounded-lg hover:bg-[#003558] transition-all duration-200"
            >
              <List className="w-4 h-4" />
              <span>View Approvals</span>
            </button>
            <button
              onClick={handleTodayClick}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-gray-700 transition-all duration-200 backdrop-blur-sm border border-white/30"
            >
              Today
            </button>
            <button
              onClick={handlePrevClick}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-lg text-gray-700 transition-all duration-200 backdrop-blur-sm border border-white/30"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={handleNextClick}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-lg text-gray-700 transition-all duration-200 backdrop-blur-sm border border-white/30"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* View Controls */}
        <div className="flex items-center space-x-2">
          {['dayGridMonth', 'timeGridWeek', 'timeGridDay'].map((view) => (
            <button
              key={view}
              onClick={() => handleViewChange(view)}
              className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                currentView === view
                  ? 'bg-[var(--polstrat-primary)] text-white shadow-lg'
                  : 'bg-white/20 hover:bg-white/30 text-gray-700 border border-white/30 transition-all duration-300 transform hover:scale-[1.01] hover:shadow-xl hover:border-blue-300/40'
              }`}
            >
              {view === 'dayGridMonth'
                ? 'Month'
                : view === 'timeGridWeek'
                ? 'Week'
                : view === 'timeGridDay'
                ? 'Day'
                : 'List'}
            </button>
          ))}
        </div>
      </div>

      {/* Calendar Body */}
      <div className="p-6 h-full">
        <div className="calendar-container bg-white/10 backdrop-blur-sm rounded-lg">
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
            initialView={currentView}
            events={allEvents}
            dateClick={(info) => {
              // Check if date is blocked before allowing click
              if (isTimeBlocked(info.dateStr)) {
                toast.error('This date is blocked');
                return;
              }
              const clickedDate = new Date(info.dateStr);
              setHeaderDate(clickedDate);                   
            }}
            selectable={true}
            selectMirror={true}
            select={handleDateSelect}
            selectAllow={(selectInfo) => {
              // Prevent selection of blocked times
              const selectedDate = selectInfo.startStr.split('T')[0];
              const startTime = new Date(selectInfo.start).toTimeString().slice(0, 5);
              const endTime = new Date(selectInfo.end).toTimeString().slice(0, 5);
              
              return !isTimeBlocked(selectedDate, startTime, endTime);
            }}
            headerToolbar={false}
            height="auto"
            dayMaxEvents={true}
            weekends={true}
            dayHeaderClassNames="font-semibold text-gray-700"
            eventClassNames="cursor-pointer hover:opacity-80 transition-opacity"
            dayCellClassNames={(arg) => {
              const isBlocked = isTimeBlocked(arg.date);
              return isBlocked 
                ? "bg-gray-200 cursor-not-allowed opacity-50" 
                : "hover:bg-black/5 transition-colors";
            }}
            slotMinTime="06:00:00"
            slotMaxTime="22:00:00"
            allDaySlot={false}
            eventDidMount={injectUnblockBtn} 
            eventClick={(info) => {
              const eventId = info.event.id;
              if (!eventId.startsWith('dnd-')) onEventClick(eventId);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default CalendarComponent;
